import React from 'react';
import { Link } from 'react-router-dom';
import EditButton from '../../../components/ui/EditButton';
import { getPendingMemberDisplayName } from '../../../utils/waitlist';

function toRoleRequestLink(teamId, request) {
  const params = new URLSearchParams({
    event: request.calendarEventId || request.eventDocId,
    panel: 'roles',
  });
  if (request.occurrenceId) params.set('occurrence', request.occurrenceId);
  return `/teams/${teamId}/calendar?${params.toString()}`;
}

function toMembershipRequestLink(teamId, request) {
  const params = new URLSearchParams({
    panel: 'members',
    member: request.requestedBy || request.id,
    request: 'membership',
  });
  return `/teams/${teamId}?${params.toString()}`;
}

function summarizeRequestMessage(message) {
  const text = typeof message === 'string' ? message.trim() : '';
  if (!text) return 'Requested a membership change.';
  if (text.length <= 96) return text;
  return `${text.slice(0, 93).trim()}...`;
}

function formatDateTime(value) {
  const date = value?.toDate?.() instanceof Date ? value.toDate() : null;
  if (!date) return '';
  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export default function AdminActionsCard({
  teamId,
  pendingMembers = [],
  waitlistMembers = [],
  learnToRowApprovalMembers = [],
  learnToSailApprovalMembers = [],
  pendingMembershipRequests = [],
  membershipRequestsLoading = false,
  pendingRoleRequests = [],
  roleRequestsLoading = false,
  onOpenPendingMembers,
  onOpenWaitlist,
  onOpenLearnToRowWaitlist,
  onOpenLearnToSailWaitlist,
  memberPreviewLink,
  joinFormDemoLink,
  canDeleteTeam = false,
  onDeleteTeam,
  pendingDeletion = null,
  canCancelDeleteTeam = false,
  onCancelDeleteTeam,
  deleteBusy = false,
  cancelDeleteBusy = false,
}) {
  const joinCount = pendingMembers.length;
  const waitlistCount = waitlistMembers.length;
  const learnToRowApprovalCount = learnToRowApprovalMembers.length;
  const learnToSailApprovalCount = learnToSailApprovalMembers.length;
  const courseApprovalCount = learnToRowApprovalCount + learnToSailApprovalCount;
  const membershipCount = pendingMembershipRequests.length;
  const roleCount = pendingRoleRequests.length;
  const pendingDeletionActive = Boolean(pendingDeletion?.executeAfter);
  const totalCount = joinCount + waitlistCount + courseApprovalCount + membershipCount + roleCount + (pendingDeletionActive ? 1 : 0);
  const deleteDueAt = formatDateTime(pendingDeletion?.executeAfter);
  const requestedBy = pendingDeletion?.requestedByName || 'An admin';
  const primaryEditSection = joinCount > 0 ? 'pendingMembers' : (learnToRowApprovalCount > 0 ? 'learnToRow' : (learnToSailApprovalCount > 0 ? 'learnToSail' : 'waitlist'));
  const handlePrimaryEdit = joinCount > 0
    ? onOpenPendingMembers
    : (learnToRowApprovalCount > 0
      ? onOpenLearnToRowWaitlist
      : (learnToSailApprovalCount > 0 ? onOpenLearnToSailWaitlist : onOpenWaitlist));

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">
          Team Admin Actions{totalCount > 0 ? ` (${totalCount})` : ''}
        </span>
        <EditButton section={primaryEditSection} onEdit={handlePrimaryEdit} />
      </div>

      <div className="card-body">
        {pendingDeletionActive && (
          <div
            style={{
              marginBottom: 12,
              padding: '0.85rem',
              borderRadius: 8,
              background: '#fff1f2',
              border: '1px solid #fecdd3',
              color: '#881337',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Deletion scheduled</div>
            <div className="small" style={{ lineHeight: 1.5 }}>
              {requestedBy} scheduled this club for deletion{deleteDueAt ? ` on ${deleteDueAt}` : ''}.
            </div>
            <div className="small" style={{ lineHeight: 1.5, marginTop: 4 }}>
              All admins should already have received an urgent email warning.
            </div>
            {canCancelDeleteTeam && (
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => onCancelDeleteTeam?.()}
                  disabled={cancelDeleteBusy}
                >
                  {cancelDeleteBusy ? 'Cancelling…' : 'Cancel Delete'}
                </button>
              </div>
            )}
          </div>
        )}

        {(memberPreviewLink || joinFormDemoLink) && (
          <>
            <div className="text-muted small" style={{ marginBottom: 6 }}>
              Quick tools
            </div>
            <div className="admin-quick-tools" style={{ marginBottom: 10 }}>
              {memberPreviewLink && (
                <Link to={memberPreviewLink} className="btn btn--ghost btn--sm">
                  See as Member
                </Link>
              )}
              {joinFormDemoLink && (
                <Link to={joinFormDemoLink} className="btn btn--ghost btn--sm">
                  Demo Join Form
                </Link>
              )}
              {canDeleteTeam && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => onDeleteTeam?.()}
                  disabled={deleteBusy}
                  style={{ color: '#b42318', borderColor: '#f4b4ae' }}
                >
                  {deleteBusy ? 'Scheduling…' : 'Delete Team'}
                </button>
              )}
            </div>
          </>
        )}

        {joinCount > 0 && (
          <>
            <div className="text-muted small" style={{ marginBottom: 6 }}>
              Pending join requests
            </div>
            <ul className="member-list" style={{ marginTop: 0 }}>
              {pendingMembers.slice(0, 5).map((member) => {
                const displayName = getPendingMemberDisplayName(member);
                return (
                  <li
                    key={member.id}
                    className="member-list-item clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenPendingMembers?.()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenPendingMembers?.();
                      }
                    }}
                  >
                    <div className="member-row">
                      <strong className="member-name">{displayName}</strong>
                    </div>
                  </li>
                );
              })}
              {joinCount > 5 && (
                <li className="text-muted small">+ {joinCount - 5} more join requests</li>
              )}
            </ul>
          </>
        )}

        {waitlistCount > 0 && (
          <>
            <div className="text-muted small" style={{ marginBottom: 6, marginTop: joinCount > 0 ? 10 : 0 }}>
              Waitlist
            </div>
            <ul className="member-list" style={{ marginTop: 0 }}>
              {waitlistMembers.slice(0, 5).map((member, index) => {
                const displayName = getPendingMemberDisplayName(member);
                return (
                  <li
                    key={member.id}
                    className="member-list-item clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenLearnToSailWaitlist?.()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenLearnToSailWaitlist?.();
                      }
                    }}
                  >
                    <div className="member-row">
                      <strong className="member-name">#{index + 1} {displayName}</strong>
                    </div>
                  </li>
                );
              })}
              {waitlistCount > 5 && (
                <li className="text-muted small">+ {waitlistCount - 5} more waitlisted members</li>
              )}
            </ul>
          </>
        )}

        {learnToRowApprovalCount > 0 && (
          <>
            <div className="text-muted small" style={{ marginBottom: 6, marginTop: joinCount > 0 || waitlistCount > 0 ? 10 : 0 }}>
              Learn to Row approvals
            </div>
            <ul className="member-list" style={{ marginTop: 0 }}>
              {learnToRowApprovalMembers.slice(0, 5).map((member) => {
                const displayName =
                  member.displayName
                  || [member.firstName, member.lastName].filter(Boolean).join(' ').trim()
                  || member.formResponses?.find((r) => r.id === 'fullName')?.value
                  || 'Applicant';
                return (
                  <li
                    key={member.id}
                    className="member-list-item clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenLearnToRowWaitlist?.()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenLearnToRowWaitlist?.();
                      }
                    }}
                  >
                    <div className="member-row">
                      <strong className="member-name">{displayName}</strong>
                    </div>
                  </li>
                );
              })}
              {learnToRowApprovalCount > 5 && (
                <li className="text-muted small">+ {learnToRowApprovalCount - 5} more Learn to Row approvals</li>
              )}
            </ul>
          </>
        )}

        {learnToSailApprovalCount > 0 && (
          <>
            <div className="text-muted small" style={{ marginBottom: 6, marginTop: joinCount > 0 || waitlistCount > 0 || learnToRowApprovalCount > 0 ? 10 : 0 }}>
              Learn to Sail approvals
            </div>
            <ul className="member-list" style={{ marginTop: 0 }}>
              {learnToSailApprovalMembers.slice(0, 5).map((member) => {
                const displayName =
                  member.displayName
                  || [member.firstName, member.lastName].filter(Boolean).join(' ').trim()
                  || member.formResponses?.find((r) => r.id === 'fullName')?.value
                  || 'Applicant';
                return (
                  <li
                    key={member.id}
                    className="member-list-item clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenWaitlist?.()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenWaitlist?.();
                      }
                    }}
                  >
                    <div className="member-row">
                      <strong className="member-name">{displayName}</strong>
                    </div>
                  </li>
                );
              })}
              {learnToSailApprovalCount > 5 && (
                <li className="text-muted small">+ {learnToSailApprovalCount - 5} more Learn to Sail approvals</li>
              )}
            </ul>
          </>
        )}

        <div className="text-muted small" style={{ marginBottom: 6, marginTop: joinCount > 0 || waitlistCount > 0 || courseApprovalCount > 0 ? 10 : 0 }}>
          Pending membership requests
        </div>
        {membershipRequestsLoading ? (
          <p className="text-muted small" style={{ marginTop: 0 }}>
            Loading membership requests...
          </p>
        ) : membershipCount > 0 ? (
          <ul className="member-list" style={{ marginTop: 0 }}>
            {pendingMembershipRequests.slice(0, 6).map((request) => (
              <li key={request.id}>
                <Link
                  to={toMembershipRequestLink(teamId, request)}
                  className="member-list-item clickable"
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    padding: '0.5rem 0',
                  }}
                >
                  <div className="member-row">
                    <strong className="member-name">{request.requestedByName || 'Member'}</strong>
                    <div className="text-muted small member-meta">
                      {summarizeRequestMessage(request.message)}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
            {membershipCount > 6 && (
              <li className="text-muted small">+ {membershipCount - 6} more membership requests</li>
            )}
          </ul>
        ) : (
          <p className="text-muted small" style={{ marginTop: 0 }}>
            No pending membership requests.
          </p>
        )}

        <div className="text-muted small" style={{ marginBottom: 6, marginTop: joinCount > 0 || waitlistCount > 0 || membershipCount > 0 ? 10 : 0 }}>
          Pending role requests
        </div>
        {roleRequestsLoading ? (
          <p className="text-muted small" style={{ marginTop: 0 }}>
            Loading role requests...
          </p>
        ) : roleCount > 0 ? (
          <ul className="member-list" style={{ marginTop: 0 }}>
            {pendingRoleRequests.slice(0, 8).map((request) => (
              <li key={request.id}>
                <Link
                  to={toRoleRequestLink(teamId, request)}
                  className="member-list-item clickable"
                  style={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    padding: '0.5rem 0',
                  }}
                >
                  <div className="member-row">
                    <strong className="member-name">{request.requestedByName || 'Member'}</strong>
                    <div className="text-muted small member-meta">
                      {request.roleName} for {request.eventName}
                      {request.scope === 'occurrence' && request.occurrenceId
                        ? ` (${request.occurrenceId})`
                        : ''}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
            {roleCount > 8 && (
              <li className="text-muted small">+ {roleCount - 8} more role requests</li>
            )}
          </ul>
        ) : (
          <p className="text-muted small" style={{ marginTop: 0 }}>
            No pending role requests.
          </p>
        )}

        {totalCount === 0 && !roleRequestsLoading && !membershipRequestsLoading && (
          <p className="text-muted small" style={{ marginTop: 8 }}>
            No admin actions to take right now.
          </p>
        )}
      </div>
    </div>
  );
}
